import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { SnapshotCompare } from "./SnapshotCompare";
import { 
  Image,
  Globe,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  FolderOpen,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  ImageOff,
  Upload,
  Search,
  Download,
  Folder,
  Filter
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Bucket Card Component
const BucketCard = ({ label, count, color, icon: Icon, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "bg-zinc-900 border rounded-sm p-4 text-left transition-all",
      isActive ? `border-${color}-500 ring-1 ring-${color}-500/50` : "border-zinc-800 hover:border-zinc-600"
    )}
    data-testid={`bucket-${label}`}
  >
    <div className="flex items-center gap-3">
      <Icon className={cn("w-5 h-5", `text-${color}-500`)} />
      <div>
        <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">{label}</p>
        <p className={cn("text-2xl font-mono font-bold", `text-${color}-500`)}>{count}</p>
      </div>
    </div>
  </button>
);

// Monitor Card Component
const MonitorCard = ({ monitor, onRefresh, onDelete, onSelect, isSelected }) => {
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/image-monitor/${monitor.id}/stats`);
        setStats(response.data);
      } catch (e) {
        console.error("Failed to fetch stats:", e);
      } finally {
        setIsLoadingStats(false);
      }
    };
    fetchStats();
  }, [monitor.id, monitor.status]);

  const getStatusIcon = () => {
    switch (monitor.status) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "CRAWLING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "ERROR":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-zinc-500" />;
    }
  };

  const progress = monitor.total_urls > 0 
    ? Math.round((monitor.crawled_urls / monitor.total_urls) * 100) 
    : 0;

  return (
    <Card 
      className={cn(
        "bg-zinc-900 border rounded-sm cursor-pointer transition-all",
        isSelected ? "border-green-500 ring-1 ring-green-500/50" : "border-zinc-800 hover:border-zinc-600"
      )}
      onClick={() => onSelect(monitor)}
      data-testid={`monitor-card-${monitor.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Globe className="w-5 h-5 text-zinc-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-mono text-sm text-white truncate">{monitor.name}</p>
              <p className="text-xs text-zinc-500 truncate">{monitor.domain}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {getStatusIcon()}
          </div>
        </div>

        {monitor.status === "CRAWLING" && (
          <div className="mb-3">
            <Progress value={progress} className="h-1" />
            <p className="text-xs text-zinc-500 font-mono mt-1">
              {monitor.crawled_urls} / {monitor.total_urls} pages
            </p>
          </div>
        )}

        {stats && !isLoadingStats && (
          <div className="grid grid-cols-5 gap-1 mb-3">
            <div className="text-center p-1 bg-zinc-800 rounded-sm">
              <p className="text-xs text-green-500 font-mono font-bold">{stats.four_plus}</p>
              <p className="text-[10px] text-zinc-500">4+</p>
            </div>
            <div className="text-center p-1 bg-zinc-800 rounded-sm">
              <p className="text-xs text-blue-500 font-mono font-bold">{stats.three}</p>
              <p className="text-[10px] text-zinc-500">3</p>
            </div>
            <div className="text-center p-1 bg-zinc-800 rounded-sm">
              <p className="text-xs text-amber-500 font-mono font-bold">{stats.two}</p>
              <p className="text-[10px] text-zinc-500">2</p>
            </div>
            <div className="text-center p-1 bg-zinc-800 rounded-sm">
              <p className="text-xs text-orange-500 font-mono font-bold">{stats.one}</p>
              <p className="text-[10px] text-zinc-500">1</p>
            </div>
            <div className="text-center p-1 bg-zinc-800 rounded-sm">
              <p className="text-xs text-red-500 font-mono font-bold">{stats.zero}</p>
              <p className="text-[10px] text-zinc-500">0</p>
            </div>
          </div>
        )}

        {monitor.last_crawled && (
          <p className="text-xs text-zinc-600 font-mono mb-3">
            Last: {new Date(monitor.last_crawled).toLocaleString()}
          </p>
        )}

        {monitor.error_message && (
          <p className="text-xs text-red-500 font-mono mb-3 truncate">
            {monitor.error_message}
          </p>
        )}

        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <SnapshotCompare 
            siteId={monitor.id} 
            siteName={monitor.name} 
            monitorType="image"
            buttonClassName="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-xs border-zinc-700"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRefresh(monitor.id, false)}
            disabled={monitor.status === "CRAWLING"}
            className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm font-mono text-xs"
            data-testid={`refresh-monitor-${monitor.id}`}
            title="Check for new pages"
          >
            <RefreshCw className={cn("w-4 h-4 mr-1", monitor.status === "CRAWLING" && "animate-spin")} />
            New
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRefresh(monitor.id, true)}
            disabled={monitor.status === "CRAWLING"}
            className="flex-1 text-blue-400 hover:text-blue-300 hover:bg-blue-950/30 rounded-sm font-mono text-xs"
            data-testid={`full-refresh-monitor-${monitor.id}`}
            title="Re-crawl all pages"
          >
            <RefreshCw className={cn("w-4 h-4 mr-1", monitor.status === "CRAWLING" && "animate-spin")} />
            Full
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(monitor.id)}
            className="text-red-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm font-mono text-xs"
            data-testid={`delete-monitor-${monitor.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Page List Component
const PageList = ({ monitorId, bucket, sitemapType, monitorName, onExportCsv }) => {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPage, setExpandedPage] = useState(null);

  useEffect(() => {
    const fetchPages = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (bucket) params.append('bucket', bucket);
        if (sitemapType && sitemapType !== 'all') params.append('sitemap_type', sitemapType);
        const queryString = params.toString() ? `?${params.toString()}` : '';
        const response = await axios.get(`${API}/image-monitor/${monitorId}/pages${queryString}`);
        setPages(response.data);
      } catch (e) {
        console.error("Failed to fetch pages:", e);
        toast.error("Failed to load pages");
      } finally {
        setIsLoading(false);
      }
    };
    
    if (monitorId) {
      fetchPages();
    }
  }, [monitorId, bucket, sitemapType]);

  const getBucketLabel = () => {
    switch (bucket) {
      case "4plus": return "4+ Images";
      case "3": return "3 Images";
      case "2": return "2 Images";
      case "1": return "1 Image";
      case "0": return "No Images";
      default: return "All Pages";
    }
  };

  const getBucketColor = () => {
    switch (bucket) {
      case "4plus": return "text-green-500";
      case "3": return "text-blue-500";
      case "2": return "text-amber-500";
      case "1": return "text-orange-500";
      case "0": return "text-red-500";
      default: return "text-zinc-400";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
            <div className="h-4 bg-zinc-800 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FolderOpen className={cn("w-5 h-5", getBucketColor())} />
          <h3 className="font-mono text-sm text-white">{getBucketLabel()}</h3>
          <span className="text-xs text-zinc-500 font-mono">({pages.length} pages)</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onExportCsv(bucket)}
          disabled={pages.length === 0}
          className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-xs h-7"
          data-testid="export-image-csv-btn"
        >
          <Download className="w-3 h-3 mr-1" />
          Export CSV
        </Button>
      </div>

      {pages.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-zinc-800 rounded-sm">
          <ImageOff className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-zinc-500 font-mono text-sm">No pages in this category</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
          {pages.map((page) => (
            <div 
              key={page.id}
              className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden"
            >
              <div 
                className="p-3 flex items-center justify-between cursor-pointer hover:bg-zinc-800/50"
                onClick={() => setExpandedPage(expandedPage === page.id ? null : page.id)}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className={cn(
                    "w-6 h-6 rounded-sm flex items-center justify-center font-mono text-xs font-bold",
                    page.image_count >= 4 ? "bg-green-900/50 text-green-400" :
                    page.image_count === 3 ? "bg-blue-900/50 text-blue-400" :
                    page.image_count === 2 ? "bg-amber-900/50 text-amber-400" :
                    page.image_count === 1 ? "bg-orange-900/50 text-orange-400" :
                    "bg-red-900/50 text-red-400"
                  )}>
                    {page.image_count}
                  </div>
                  <a 
                    href={page.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-zinc-300 hover:text-white truncate font-mono"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {page.url}
                  </a>
                  {page.is_new && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/50 text-blue-400 rounded font-mono">
                      NEW
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-500 hover:text-white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  {expandedPage === page.id ? (
                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                  )}
                </div>
              </div>
              
              {expandedPage === page.id && page.images && page.images.length > 0 && (
                <div className="px-3 pb-3 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 font-mono mt-2 mb-2">Images found:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {page.images.slice(0, 8).map((img, idx) => (
                      <div key={idx} className="aspect-square bg-zinc-800 rounded-sm overflow-hidden">
                        <img 
                          src={img.startsWith('http') ? img : `${page.url}${img}`}
                          alt={`Image ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {page.images.length > 8 && (
                    <p className="text-xs text-zinc-500 font-mono mt-2">
                      +{page.images.length - 8} more images
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Image Monitor Component
export const ImageMonitor = () => {
  const [monitors, setMonitors] = useState([]);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [selectedSitemapType, setSelectedSitemapType] = useState("all");
  const [stats, setStats] = useState(null);
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]); // For adding new sites
  const [filterGroup, setFilterGroup] = useState("all"); // For filtering sites

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
    } catch (e) {
      console.error("Failed to fetch groups:", e);
    }
  }, []);

  // Filter monitors based on search query and group
  const filteredMonitors = monitors.filter((monitor) => {
    // Group filter
    if (filterGroup !== "all") {
      if (!monitor.groups || !monitor.groups.includes(filterGroup)) {
        return false;
      }
    }
    // Search filter
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      monitor.name?.toLowerCase().includes(query) ||
      monitor.domain?.toLowerCase().includes(query) ||
      monitor.sitemap_url?.toLowerCase().includes(query)
    );
  });

  const fetchMonitors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/image-monitor`);
      setMonitors(response.data);
      
      // Auto-select first monitor if none selected
      if (response.data.length > 0 && !selectedMonitor) {
        setSelectedMonitor(response.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch monitors:", e);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonitor]);

  const fetchStats = useCallback(async () => {
    if (!selectedMonitor) {
      setStats(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/image-monitor/${selectedMonitor.id}/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
      setStats(null);
    }
  }, [selectedMonitor?.id]);

  useEffect(() => {
    fetchMonitors();
    fetchGroups();
    const interval = setInterval(fetchMonitors, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [fetchMonitors, fetchGroups]);

  useEffect(() => {
    fetchStats();
    // Reset sitemap type filter when monitor changes
    setSelectedSitemapType("all");
  }, [fetchStats, selectedMonitor?.id]);

  const handleAddMonitor = async (e) => {
    e.preventDefault();
    if (!sitemapUrl.trim()) return;

    setIsAdding(true);
    try {
      const response = await axios.post(`${API}/image-monitor`, {
        sitemap_url: sitemapUrl,
        name: siteName || undefined,
        groups: selectedGroups
      });
      toast.success("Sitemap added - crawling started");
      setSitemapUrl("");
      setSiteName("");
      setSelectedGroups([]);
      setSelectedMonitor(response.data);
      fetchMonitors();
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to add sitemap";
      toast.error(message);
    } finally {
      setIsAdding(false);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleRefresh = async (monitorId, full = false) => {
    try {
      await axios.post(`${API}/image-monitor/${monitorId}/refresh${full ? '?full=true' : ''}`);
      toast.success(full ? "Full re-crawl started" : "Refresh started");
      fetchMonitors();
    } catch (e) {
      toast.error("Failed to start refresh");
    }
  };

  const handleDelete = async (monitorId) => {
    try {
      await axios.delete(`${API}/image-monitor/${monitorId}`);
      toast.success("Monitor deleted");
      if (selectedMonitor?.id === monitorId) {
        setSelectedMonitor(null);
        setStats(null);
      }
      fetchMonitors();
    } catch (e) {
      toast.error("Failed to delete monitor");
    }
  };

  const handleCsvImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/image-monitor/import-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { added, skipped, errors } = response.data;
      
      if (added.length > 0) {
        toast.success(`Imported ${added.length} sitemap(s) - crawling started`);
      }
      if (skipped.length > 0) {
        toast.info(`Skipped ${skipped.length} duplicate(s)`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} URL(s) had errors`);
      }
      
      fetchMonitors();
    } catch (e) {
      const message = e.response?.data?.detail || "Failed to import CSV";
      toast.error(message);
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  const handleExportCsv = (bucket) => {
    if (!selectedMonitor) return;
    const bucketParam = bucket ? `?bucket=${bucket}` : "";
    window.open(`${API}/image-monitor/${selectedMonitor.id}/export-csv${bucketParam}`, '_blank');
    toast.success("Export started");
  };

  return (
    <div className="text-zinc-100">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#09090b]/80 border-b border-zinc-800">
        <div className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Image className="w-7 h-7 text-blue-500" />
            <div>
              <h1 className="font-mono text-lg font-bold tracking-tight">Image Monitoring</h1>
              <p className="text-xs text-zinc-500 font-mono">Monitor pages for image content</p>
            </div>
          </div>
        </div>
      </header>

      <main className="p-6">
        {/* Add Sitemap Form */}
        <section className="mb-6" data-testid="add-sitemap-section">
          <form onSubmit={handleAddMonitor} className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="url"
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                  className="pl-11 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600"
                  data-testid="sitemap-url-input"
                  required
                />
              </div>
              <Input
                type="text"
                placeholder="Site name (optional)"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                className="w-48 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600"
                data-testid="site-name-input"
              />
              <Button
                type="submit"
                disabled={isAdding || !sitemapUrl.trim()}
                className="bg-white text-black hover:bg-zinc-200 rounded-sm font-mono text-sm uppercase tracking-wider px-6"
                data-testid="add-sitemap-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {isAdding ? "Adding..." : "Add"}
              </Button>
              <div className="relative">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvImport}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="csv-sitemap-input"
                  disabled={isImporting}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isImporting}
                  className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 hover:text-white rounded-sm font-mono text-sm uppercase tracking-wider"
                  data-testid="import-csv-sitemap-btn"
                >
                  <Upload className={`w-4 h-4 mr-2 ${isImporting ? 'animate-pulse' : ''}`} />
                  {isImporting ? "Importing..." : "CSV"}
                </Button>
              </div>
            </div>
            {/* Group Selection */}
            {groups.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-zinc-500 font-mono">Assign to groups:</span>
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggleGroupSelection(group.id)}
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded text-xs font-mono transition-colors",
                      selectedGroups.includes(group.id)
                        ? "bg-zinc-700 text-white"
                        : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    )}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                    {group.name}
                  </button>
                ))}
              </div>
            )}
          </form>
          <p className="text-xs text-zinc-600 font-mono mt-2">
            CSV format: "sitemap_url" column (optionally "name" column)
          </p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monitors List */}
          <div className="lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-mono text-sm text-zinc-400">MONITORED SITEMAPS</h2>
              <span className="text-xs text-zinc-600 font-mono">{filteredMonitors.length}/{monitors.length}</span>
            </div>
            {/* Search and Group Filter */}
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder="Search sitemaps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-zinc-900 border-zinc-800 rounded-sm font-mono text-sm placeholder:text-zinc-600 h-9"
                  data-testid="search-sitemaps"
                />
              </div>
              {groups.length > 0 && (
                <select
                  value={filterGroup}
                  onChange={(e) => setFilterGroup(e.target.value)}
                  className="bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-1 text-xs text-zinc-300 font-mono h-9"
                  data-testid="group-filter"
                >
                  <option value="all">All Groups</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-zinc-800 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : monitors.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-zinc-800 rounded-sm">
                <Image className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 font-mono text-sm">No sitemaps monitored</p>
                <p className="text-zinc-600 text-xs font-mono mt-1">Add a sitemap URL above</p>
              </div>
            ) : filteredMonitors.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-zinc-800 rounded-sm">
                <Search className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-500 font-mono text-sm">No matches found</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-zinc-400 text-xs font-mono mt-2 hover:text-white"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-350px)] overflow-y-auto pr-1">
                {filteredMonitors.map((monitor) => (
                  <MonitorCard
                    key={monitor.id}
                    monitor={monitor}
                    onRefresh={handleRefresh}
                    onDelete={handleDelete}
                    onSelect={setSelectedMonitor}
                    isSelected={selectedMonitor?.id === monitor.id}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Stats & Pages */}
          <div className="lg:col-span-2">
            {selectedMonitor ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-mono text-sm text-zinc-400">
                    {selectedMonitor.name.toUpperCase()} - IMAGE BUCKETS
                  </h2>
                  {/* Sitemap Type Filter */}
                  {selectedMonitor.sitemap_types && selectedMonitor.sitemap_types.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500 font-mono">Type:</span>
                      <select
                        value={selectedSitemapType}
                        onChange={(e) => setSelectedSitemapType(e.target.value)}
                        className="bg-zinc-900 border border-zinc-700 rounded-sm text-xs font-mono px-2 py-1 text-zinc-300 focus:outline-none focus:border-zinc-500"
                        data-testid="sitemap-type-filter"
                      >
                        <option value="all">All Types</option>
                        {selectedMonitor.sitemap_types.map((type) => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                
                {stats && (
                  <div className="grid grid-cols-5 gap-3 mb-6">
                    <button
                      onClick={() => setSelectedBucket(selectedBucket === "4plus" ? null : "4plus")}
                      className={cn(
                        "bg-zinc-900 border rounded-sm p-3 text-center transition-all",
                        selectedBucket === "4plus" ? "border-green-500 ring-1 ring-green-500/50" : "border-zinc-800 hover:border-zinc-600"
                      )}
                    >
                      <p className="text-xl font-mono font-bold text-green-500">{stats.four_plus}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">4+ imgs</p>
                    </button>
                    <button
                      onClick={() => setSelectedBucket(selectedBucket === "3" ? null : "3")}
                      className={cn(
                        "bg-zinc-900 border rounded-sm p-3 text-center transition-all",
                        selectedBucket === "3" ? "border-blue-500 ring-1 ring-blue-500/50" : "border-zinc-800 hover:border-zinc-600"
                      )}
                    >
                      <p className="text-xl font-mono font-bold text-blue-500">{stats.three}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">3 imgs</p>
                    </button>
                    <button
                      onClick={() => setSelectedBucket(selectedBucket === "2" ? null : "2")}
                      className={cn(
                        "bg-zinc-900 border rounded-sm p-3 text-center transition-all",
                        selectedBucket === "2" ? "border-amber-500 ring-1 ring-amber-500/50" : "border-zinc-800 hover:border-zinc-600"
                      )}
                    >
                      <p className="text-xl font-mono font-bold text-amber-500">{stats.two}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">2 imgs</p>
                    </button>
                    <button
                      onClick={() => setSelectedBucket(selectedBucket === "1" ? null : "1")}
                      className={cn(
                        "bg-zinc-900 border rounded-sm p-3 text-center transition-all",
                        selectedBucket === "1" ? "border-orange-500 ring-1 ring-orange-500/50" : "border-zinc-800 hover:border-zinc-600"
                      )}
                    >
                      <p className="text-xl font-mono font-bold text-orange-500">{stats.one}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">1 img</p>
                    </button>
                    <button
                      onClick={() => setSelectedBucket(selectedBucket === "0" ? null : "0")}
                      className={cn(
                        "bg-zinc-900 border rounded-sm p-3 text-center transition-all",
                        selectedBucket === "0" ? "border-red-500 ring-1 ring-red-500/50" : "border-zinc-800 hover:border-zinc-600"
                      )}
                    >
                      <p className="text-xl font-mono font-bold text-red-500">{stats.zero}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">No img</p>
                    </button>
                  </div>
                )}

                <PageList 
                  monitorId={selectedMonitor.id} 
                  bucket={selectedBucket}
                  sitemapType={selectedSitemapType}
                  monitorName={selectedMonitor.name}
                  onExportCsv={handleExportCsv}
                />
              </>
            ) : (
              <div className="flex items-center justify-center h-64 border border-dashed border-zinc-800 rounded-sm">
                <div className="text-center">
                  <Image className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono">Select a sitemap to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-8">
        <div className="px-6 py-4">
          <p className="text-center text-xs text-zinc-600 font-mono">
            Sitemaps checked every 6 hours • Slack & Email alerts for new pages with &lt;3 images
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ImageMonitor;
